'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { getUserPreferredLanguage, saveUserLanguagePreference } from '../lib/geoip';

const isSupportedLocale = (locale: string | null | undefined): locale is 'en' | 'fr' =>
  locale === 'en' || locale === 'fr';

interface InlineLanguageDetectorProps {
  children: React.ReactNode;
}

export default function InlineLanguageDetector({ children }: InlineLanguageDetectorProps) {
  const [isDetecting, setIsDetecting] = useState(false);
  const detectionCompletedRef = useRef(false);
  const manualSwitchRef = useRef(false);
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('CountryDetection');

  useEffect(() => {
    let cancelled = false;

    const markDetectionComplete = () => {
      detectionCompletedRef.current = true;
      if (typeof window !== 'undefined') {
        localStorage.setItem('language-detected', 'true');
      }
    };

    async function detectAndRedirect() {
      try {
        // Persist detection completion flag across navigations
        if (typeof window !== 'undefined' && localStorage.getItem('language-detected') === 'true') {
          detectionCompletedRef.current = true;
        }

        if (!pathname) return;

        const currentLocaleMatch = pathname.match(/^\/(en|fr)/);
        const currentLocale = currentLocaleMatch ? currentLocaleMatch[1] : 'en';
        
        const savedPreference = typeof window !== 'undefined' 
          ? localStorage.getItem('user-language-preference') 
          : null;
        const hasPreference = isSupportedLocale(savedPreference);

        const redirectTo = (locale: string) => {
          const pathWithoutLocale = pathname.replace(/^\/(en|fr)/, '') || '/';
          const newPath = `/${locale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`;
          router.replace(newPath);
        };

        // Manual language switch: only show loading overlay, do not re-run detection logic
        if (manualSwitchRef.current) {
          markDetectionComplete();
          manualSwitchRef.current = false;
          setTimeout(() => setIsDetecting(false), 400);
          return;
        }

        // If user already chose a preference, ensure we stay on that locale without extra loading state
        if (hasPreference) {
          markDetectionComplete();
          if (currentLocale !== savedPreference) {
            redirectTo(savedPreference);
            return;
          }
          setIsDetecting(false);
          return;
        }

        // If we've already run detection once in this session, skip re-running
        if (detectionCompletedRef.current) {
          setIsDetecting(false);
          return;
        }

        // Initial auto-detection (first visit only)
        setIsDetecting(true);
        const preferredLanguage = await getUserPreferredLanguage();
        if (cancelled) return;

        const finalLocale: 'en' | 'fr' = isSupportedLocale(preferredLanguage) 
          ? preferredLanguage 
          : 'en';

        saveUserLanguagePreference(finalLocale);
        markDetectionComplete();

        if (currentLocale !== finalLocale) {
          redirectTo(finalLocale);
          return;
        }

        setIsDetecting(false);
      } catch (error) {
        console.error('Language detection error:', error);
        setIsDetecting(false);
      }
    }

    // Only run detection if we're on the client and have a pathname
    if (typeof window !== 'undefined' && pathname) {
      detectAndRedirect();
    }
    
    return () => {
      cancelled = true;
    };
  }, [router, pathname]);

  // Listen for language switcher clicks to show modal immediately
  useEffect(() => {
    const handleLanguageSwitch = () => {
      manualSwitchRef.current = true;
      setIsDetecting(true);
    };

    // Listen for custom language switch events
    if (typeof window !== 'undefined') {
      window.addEventListener('language-switch', handleLanguageSwitch);
      
      return () => {
        window.removeEventListener('language-switch', handleLanguageSwitch);
      };
    }
  }, []);

  return (
    <div className="relative">
      {/* Main content with blur effect when detecting */}
      <div className={`language-detection-content ${isDetecting ? 'blurred' : ''}`}>
        {children}
      </div>

      {/* Loading overlay */}
      {isDetecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center language-detection-overlay">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 max-w-sm mx-auto">
            {/* Spinner */}
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 border-4 border-brand-secondary/30 border-t-[#3F74EF] rounded-full animate-spin"></div>
            </div>
            
            {/* Loading text */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('preparingExperience')}
              </h3>
              <p className="text-sm text-gray-300">
                {t('preparingDescription')}
              </p>
            </div>
            
            {/* Progress dots */}
            <div className="flex justify-center space-x-2 rtl:space-x-reverse mt-6">
              <div className="w-2 h-2 bg-brand-secondary rounded-full animate-pulse" style={{ animationDelay: '0s' }}></div>
              <div className="w-2 h-2 bg-[#8b5cf6] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-[#a855f7] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
